import cookies from 'js-cookie';
import {apiUrl} from 'constants/app';
import {shake, defaultPredicate} from 'utils/shake';
import {removeCrossSiteCookie} from 'utils/cookies';

export const resolveApiUrl = path => `${apiUrl}${path}`;

const shakeParamsPredicate = value =>
	Array.isArray(value) ? !!value.length : defaultPredicate(value);

const createHeaders = headers => {
	const apiToken = cookies.get('api_token');
	const organizationId = cookies.get('organization_id');

	return new Headers({
		'Content-Type': 'application/json; charset=utf-8',
		...(apiToken ? {Authorization: `Bearer ${apiToken}`} : {}),
		...(organizationId ? {'organization-id': organizationId} : {}),
		...headers,
	});
};

const createRequest = method => (path, options) => {
	const url = new URL(resolveApiUrl(path));
	const {params, body, ...opts} = options;

	if (params) {
		url.search = new URLSearchParams(shake(params, shakeParamsPredicate)).toString();
	}

	return new Request(url, {
		...opts,
		body: body ? JSON.stringify(body) : undefined,
		method,
		headers: createHeaders(options.headers || {}),
	});
};

const handleResponse = resp => {
	if (resp.status === 401) {
		removeCrossSiteCookie('api_token');
		removeCrossSiteCookie('organization_id');
		window.location.assign('/');
		return;
	}

	// Throw response object if request was not successful
	if (!resp.ok) {
		throw resp;
	}

	// Return null for 204 No Content responses
	if (resp.status === 204) {
		return null;
	}

	// Check the response content type to handle binary data
	const contentType = resp?.headers?.get('content-type');

	// Handle binary data differently
	if (contentType && contentType.includes('audio/')) {
		return resp.blob();
	}

	return resp.text().then(text => {
		if (!text) return null;
		try {
			return JSON.parse(text);
		} catch {
			return text;
		}
	});
};

/**
 * API client singleton - can be used anywhere (not just React components)
 */
export const api = {
	get: (url, options = {}) =>
		fetch(createRequest('GET')(url, options)).then(handleResponse),
	put: (url, options = {}) =>
		fetch(createRequest('PUT')(url, options)).then(handleResponse),
	patch: (url, options = {}) =>
		fetch(createRequest('PATCH')(url, options)).then(handleResponse),
	post: (url, options = {}) =>
		fetch(createRequest('POST')(url, options)).then(handleResponse),
	remove: (url, options = {}) =>
		fetch(createRequest('DELETE')(url, options)).then(handleResponse),
};
